<script>
import axios from 'axios';
import Layout from '@/layouts/main.vue';
import PageHeader from '@/components/page-header';
import { api, sucesso, erro, confirmar } from '@/helpers/common/api';
import CampanhaModal from '@/components/crm/CampanhaModal.vue';
import socketClient from '@/services/socketClient';
import { baixarRelatorioDisparo } from '@/helpers/campanhaRelatorioDisparo';

export default {
  name: 'CampanhaIndex',
  components: { Layout, PageHeader, CampanhaModal },
  data() {
    return {
      campanhas: [],
      loading: false,
      modalAberto: false,
      campanhaEditando: null,

      progressoPorCampanha: {}, // id -> objeto progresso
      socketOffs: [],
      gerandoRelatorio: {}, // id -> true enquanto o arquivo é montado

      diagnosticos: {},         // id -> objeto diagnóstico (por que parou)
      destravando: {},          // id -> bool (spinner do botão)
      diagTimer: null,

      usoCanais: {},            // canal_id -> { limite, usado, saldo, ilimitado }
      metrics: {},
      loadingMetrics: false,
    };
  },
  computed: {
    porStatus() { return this.metrics.porStatus || {}; },
    temEmExecucao() { return this.campanhas.some((c) => c.status === 'em_execucao'); },
  },
  async created() {
    await Promise.all([this.carregarCampanhas(), this.carregarMetrics(), this.carregarUso()]);
    await this.carregarDiagnosticos();
    this._registrarSockets();
    // Reavalia o motivo das campanhas em execução periodicamente (o progresso
    // vem por socket; o motivo — janela, limite, worker — é recalculado aqui).
    // Acompanhamento: enquanto há campanha em execução, atualiza os indicadores
    // por polling (rede de segurança caso o realtime por socket não chegue) —
    // é o que garante que os números de envio mexam mesmo sem socket.
    this.diagTimer = setInterval(() => {
      if (!this.temEmExecucao) return;
      this.carregarCampanhas({ silencioso: true });
      this.carregarMetrics();
      this.carregarDiagnosticos();
    }, 8000);
  },
  beforeUnmount() {
    this._desregistrarSockets();
    if (this.diagTimer) clearInterval(this.diagTimer);
    if (this._refreshTimer) { clearTimeout(this._refreshTimer); this._refreshTimer = null; }
  },
  methods: {
    _registrarSockets() {
      try {
        const off1 = socketClient.on('campanha:progresso', ({ campanha_id, progresso }) => {
          this.progressoPorCampanha = { ...this.progressoPorCampanha, [campanha_id]: progresso };
          // Os indicadores da tabela leem os campos `total_*` da campanha (que o
          // backend atualiza no banco). Re-busca (debounced) pra refletir o
          // envio ao vivo sem um request por disparo.
          this._agendarRefresh();
        });
        const recarregar = () => { this.carregarCampanhas({ silencioso: true }); this.carregarMetrics(); this.carregarDiagnosticos(); };
        const off2 = socketClient.on('campanha:concluida', recarregar);
        const off3 = socketClient.on('campanha:iniciada', recarregar);
        const off4 = socketClient.on('campanha:pausada', recarregar);
        const off5 = socketClient.on('campanha:cancelada', recarregar);
        this.socketOffs = [off1, off2, off3, off4, off5];
      } catch (_e) { /* sem socket disponível */ }
    },

    // Coalesce vários eventos de progresso num único refresh a cada ~2s.
    _agendarRefresh() {
      if (this._refreshTimer) return;
      this._refreshTimer = setTimeout(() => {
        this._refreshTimer = null;
        this.carregarCampanhas({ silencioso: true });
        this.carregarMetrics();
      }, 2000);
    },
    _desregistrarSockets() {
      this.socketOffs.forEach((fn) => { try { fn && fn(); } catch (_e) { /* */ } });
      this.socketOffs = [];
    },

    async carregarCampanhas({ silencioso = false } = {}) {
      // `silencioso` evita piscar o spinner durante o polling de acompanhamento.
      if (!silencioso) this.loading = true;
      try {
        const r = await api.read('/campanha');
        this.campanhas = Array.isArray(r) ? r : [];
      } finally {
        if (!silencioso) this.loading = false;
      }
    },

    async carregarMetrics() {
      this.loadingMetrics = true;
      try {
        const r = await api.read('/campanha/total');
        if (r && typeof r === 'object') this.metrics = r;
      } finally {
        this.loadingMetrics = false;
      }
    },

    async carregarUso() {
      try {
        const r = await api.read('/canal/whatsapp/uso');
        const m = {};
        (Array.isArray(r) ? r : []).forEach((u) => { m[u.id] = u; });
        this.usoCanais = m;
      } catch (_e) { /* uso é informativo — não bloqueia a tela */ }
    },

    /** Carrega o diagnóstico ("por que parou") de todas as campanhas em execução. */
    async carregarDiagnosticos() {
      try {
        const r = await api.read('/campanha/diagnostico');
        const m = {};
        (Array.isArray(r) ? r : []).forEach((d) => { m[d.campanha_id] = d; });
        this.diagnosticos = m;
      } catch (_e) { /* diagnóstico é informativo — não bloqueia a tela */ }
    },

    diagOf(c) { return this.diagnosticos[c.id] || null; },

    /** Ícone conforme o estado: travado (vermelho), disparando (verde), aguardando (amarelo). */
    diagIcon(c) {
      const d = this.diagOf(c);
      if (!d) return 'ri-loader-4-line';
      if (d.travado) return 'ri-error-warning-line';
      if (d.motivo === 'disparando') return 'ri-send-plane-2-line';
      return 'ri-time-line';
    },

    /** Classe do badge de motivo — theme-aware (funciona em light e dark). */
    diagBadgeClass(c) {
      const d = this.diagOf(c);
      if (!d) return 'badge bg-secondary-subtle text-secondary';
      if (d.travado) return 'badge bg-danger-subtle text-danger';
      if (d.motivo === 'disparando') return 'badge bg-success-subtle text-success';
      return 'badge bg-warning-subtle text-warning';
    },

    /** true quando faz sentido oferecer "Destravar e continuar". */
    podeDestravar(c) {
      const d = this.diagOf(c);
      return !!d && d.motivo !== 'disparando' && d.motivo !== 'deveria_concluir';
    },

    /** Detalhe legível do diagnóstico para o texto secundário/tooltip. */
    diagDetalhe(c) {
      const d = this.diagOf(c);
      if (!d || !d.dados) return d ? d.acao : '';
      const dd = d.dados;
      const partes = [];
      if (dd.progresso) partes.push(`${dd.progresso.pendente} pendente(s)`);
      if (dd.limite != null) partes.push(`limite dia ${dd.usado}/${dd.limite}`);
      if (dd.presos) partes.push(`${dd.presos} preso(s)`);
      if (dd.janela) partes.push(`janela ${dd.janela.inicio}–${dd.janela.fim}`);
      return partes.join(' · ') || d.acao;
    },

    async destravar(c) {
      this.destravando = { ...this.destravando, [c.id]: true };
      try {
        // axios cru (não api.create): precisamos do corpo do erro (409) e do
        // objeto { acoes, antes, depois } no sucesso — o wrapper devolveria só false.
        const { data } = await axios.post(`/campanha/destravar/${c.id}`);
        const acoes = (data && data.acoes) || [];
        sucesso({
          title: 'Campanha destravada',
          text: acoes.join('  •  ') || 'Reavaliada — segue conforme o diagnóstico.',
          timer: 6000,
          showConfirmButton: true,
        });
        await Promise.all([this.carregarCampanhas(), this.carregarDiagnosticos()]);
      } catch (e) {
        const corpo = e?.response?.data || {};
        erro({ title: corpo.error || 'Erro ao destravar', text: corpo.details || '' });
      } finally {
        this.destravando = { ...this.destravando, [c.id]: false };
      }
    },

    statusBadge(status) {
      const map = {
        rascunho: 'badge bg-secondary-subtle text-secondary',
        validando: 'badge bg-info-subtle text-info',
        pronta: 'badge bg-primary-subtle text-primary',
        em_execucao: 'badge bg-warning-subtle text-warning',
        pausada: 'badge bg-dark-subtle text-dark',
        concluida: 'badge bg-success-subtle text-success',
        cancelada: 'badge bg-danger-subtle text-danger',
      };
      return map[status] || 'badge bg-secondary-subtle text-secondary';
    },

    /** Rótulo do tipo de disparo (canal + provedor). */
    canalLabel(c) {
      const map = {
        meta_oficial: 'WhatsApp Oficial',
        baileys_rodizio: 'Rodízio de chips',
        allcance_massivo: 'Disparo WhatsApp',
        allcance_botao: 'WhatsApp Botão',
        allcance_sms: 'SMS (Allcance)',
        allcance_rcs_basico: 'RCS Básico',
        allcance_rcs_simples: 'RCS Simples',
      };
      return map[c.provedor] || (c.canal || '—');
    },

    abrirNova() {
      this.campanhaEditando = null;
      this.modalAberto = true;
    },
    abrirEdicao(c) {
      this.campanhaEditando = c;
      this.modalAberto = true;
    },
    onCampanhaSalva() {
      this.modalAberto = false;
      this.campanhaEditando = null;
      this.carregarCampanhas();
      this.carregarMetrics();
    },

    async validarPublico(c) {
      const r = await api.create(`/campanha/validarPublico/${c.id}`);
      if (r) {
        sucesso({
          title: `Elegíveis: ${r.publicoElegivel} de ${r.publicoEstimado} estimados (${r.bloqueadosSemConsentimento} ${c.filtros && c.filtros.exigirConsentimento === true ? 'sem opt-in' : 'com opt-out'}, ${r.semTelefone || 0} sem telefone)`,
          timer: 5000,
          showConfirmButton: true,
        });
        this.carregarCampanhas();
      } else {
        erro({ title: 'Erro ao validar público' });
      }
    },

    /**
     * POST /campanha/iniciar com o motivo real da recusa na tela.
     *
     * O helper `api` engole o corpo do erro e devolve false — o usuario via
     * so "Erro ao iniciar campanha" sem saber se faltou canal, template,
     * publico ou se o status nao permitia. Aqui o axios e usado direto para
     * ler `error`/`details` da resposta. Sem timeout do lado do cliente:
     * enfileirar milhares de destinatarios pode demorar.
     */
    async _postIniciar(c) {
      try {
        const { data } = await axios.post(`/campanha/iniciar/${c.id}`, {}, { timeout: 0 });
        const r = (data && Object.prototype.hasOwnProperty.call(data, 'data')) ? data.data : data;
        sucesso({ title: `Campanha iniciada — ${(r && r.totalDestinatarios) || 0} destinatário(s) na fila.` });
      } catch (e) {
        const resp = e && e.response;
        const motivo = (resp && resp.data && (resp.data.error || resp.data.details))
          || (e && e.message)
          || 'Erro desconhecido.';
        erro({ title: 'Não foi possível iniciar a campanha', text: motivo });
      } finally {
        this.carregarCampanhas();
        this.carregarDiagnosticos();
      }
    },

    async iniciar(c) {
      if (String(c.provedor || '').startsWith('allcance')) {
        this._iniciarAllcance(c);
        return;
      }
      if (this.ehRodizio(c)) {
        const elegiveis = c.publico_elegivel || 0;
        confirmar({
          options: {
            title: elegiveis ? `Iniciar disparo para ${elegiveis} contato(s)?` : 'Calcular público e iniciar disparo?',
            text: 'O envio é distribuído entre os chips de disparo conectados, respeitando o intervalo e o teto diário de cada chip e a janela horária da campanha. O que não couber hoje segue nos próximos dias.',
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: 'Sim, iniciar',
            customClass: { confirmButton: 'btn btn-success w-xs me-2', cancelButton: 'btn btn-danger w-xs' },
            buttonsStyling: false,
          },
          confirmado: async () => { await this._postIniciar(c); },
        });
        return;
      }
      await this.carregarUso();
      const elegiveis = c.publico_elegivel || 0;
      const podeIniciarSemPreview = elegiveis > 0;
      const titulo = podeIniciarSemPreview
        ? `Iniciar disparo para ${elegiveis} contatos?`
        : 'Calcular público e iniciar disparo?';

      // Aviso de limite diário Meta: alerta se o público não cabe no saldo do dia.
      const uso = c.canal_whatsapp_id ? this.usoCanais[c.canal_whatsapp_id] : null;
      let avisoLimite = '';
      if (uso && !uso.ilimitado && uso.saldo != null) {
        if (uso.saldo <= 0) {
          avisoLimite = ` ⚠️ Este número já atingiu o limite diário da Meta (${uso.usado}/${uso.limite}); o envio só começa amanhã.`;
        } else if (elegiveis > uso.saldo) {
          avisoLimite = ` ⚠️ Só ${uso.saldo} de ${elegiveis} serão enviados hoje (limite Meta ${uso.usado}/${uso.limite}); o restante segue nos próximos dias automaticamente.`;
        }
      }

      confirmar({
        options: {
          title: titulo,
          text: `O worker vai respeitar janela horária, intervalo entre msgs e limite diário Meta.${avisoLimite}`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Sim, iniciar',
          customClass: { confirmButton: 'btn btn-success w-xs me-2', cancelButton: 'btn btn-danger w-xs' },
          buttonsStyling: false,
        },
        confirmado: async () => { await this._postIniciar(c); },
      });
    },

    /** Confirmação de início dos disparos Allcance (lote único, sem limite Meta). */
    _iniciarAllcance(c) {
      const MIN = { allcance_massivo: 2000, allcance_botao: 10000 };
      const TIPO = {
        allcance_massivo: 'Disparo via WhatsApp',
        allcance_botao: 'WhatsApp com Botão',
        allcance_sms: 'SMS',
        allcance_rcs_basico: 'RCS Básico',
        allcance_rcs_simples: 'RCS Simples',
      };
      const minimo = MIN[c.provedor] || 0;
      const elegiveis = c.publico_elegivel || 0;
      const tipo = TIPO[c.provedor] || 'Allcance';
      let aviso = '';
      if (minimo && elegiveis && elegiveis < minimo) {
        aviso = ` ⚠️ Público atual (${elegiveis}) está abaixo do mínimo de ${minimo.toLocaleString('pt-BR')} — a Allcance pode recusar o envio.`;
      }
      confirmar({
        options: {
          title: `Iniciar ${tipo} para ${elegiveis} contato(s)?`,
          text: `Envio em lote único pela Allcance — a mesma mensagem vai para todos.${aviso}`,
          icon: 'question',
          showCancelButton: true,
          confirmButtonText: 'Sim, iniciar',
          customClass: { confirmButton: 'btn btn-success w-xs me-2', cancelButton: 'btn btn-danger w-xs' },
          buttonsStyling: false,
        },
        confirmado: async () => { await this._postIniciar(c); },
      });
    },

    async pausar(c) {
      const r = await api.create(`/campanha/pausar/${c.id}`);
      if (r) { sucesso({ title: 'Campanha pausada.' }); this.carregarCampanhas(); }
      else erro({ title: 'Erro ao pausar' });
    },
    async retomar(c) {
      const r = await api.create(`/campanha/retomar/${c.id}`);
      if (r) { sucesso({ title: 'Campanha retomada.' }); this.carregarCampanhas(); }
      else erro({ title: 'Erro ao retomar' });
    },
    async cancelar(c) {
      confirmar({
        options: {
          title: `Cancelar campanha "${c.nome}"?`,
          text: 'Itens pendentes serão marcados como cancelados.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, cancelar',
          customClass: { confirmButton: 'btn btn-danger w-xs me-2', cancelButton: 'btn btn-secondary w-xs' },
          buttonsStyling: false,
        },
        confirmado: async () => {
          const r = await api.create(`/campanha/cancelar/${c.id}`);
          if (r) { sucesso({ title: 'Campanha cancelada.' }); this.carregarCampanhas(); }
          else erro({ title: 'Erro ao cancelar' });
        },
      });
    },

    /**
     * Exclui a campanha (e a fila junto, por cascata no banco).
     *
     * O backend recusa com 409 se a campanha já disparou ou está rodando — daí
     * o axios cru em vez do `api.destroy`: o wrapper devolve só `false` e a
     * razão da recusa, que é o que interessa aqui, se perderia.
     */
    async excluir(c) {
      confirmar({
        options: {
          title: `Excluir campanha "${c.nome}"?`,
          text: 'A campanha e a fila dela são apagadas. Não dá para desfazer.',
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Sim, excluir',
          customClass: { confirmButton: 'btn btn-danger w-xs me-2', cancelButton: 'btn btn-secondary w-xs' },
          buttonsStyling: false,
        },
        confirmado: async () => {
          try {
            await axios.delete(`/campanha/${c.id}`);
            sucesso({ title: 'Campanha excluída.' });
            this.carregarCampanhas();
            this.carregarMetrics();
          } catch (e) {
            const corpo = e?.response?.data || {};
            erro({ title: corpo.error || 'Erro ao excluir', text: corpo.details || '' });
          }
        },
      });
    },

    /** Rodízio de chips é envio puro: sem painel/indicadores de resposta. */
    ehRodizio(c) {
      return c && c.provedor === 'baileys_rodizio';
    },
    async relatorioDisparo(c, formato) {
      this.gerandoRelatorio = { ...this.gerandoRelatorio, [c.id]: true };
      try {
        await baixarRelatorioDisparo(c.id, formato);
      } finally {
        this.gerandoRelatorio = { ...this.gerandoRelatorio, [c.id]: false };
      }
    },

    /** Painel de retorno da campanha (quem respondeu, o quê, e o relatório). */
    verRespostas(c) {
      this.$router.push({ path: '/app/campanha/respostas', query: { id: c.id } });
    },
    pctEnviado(c) {
      if (!c.total_destinatarios) return 0;
      return Math.round((Number(c.total_enviados || 0) / c.total_destinatarios) * 100);
    },
    pctFalha(c) {
      if (!c.total_destinatarios) return 0;
      return Math.round((Number(c.total_falhas || 0) / c.total_destinatarios) * 100);
    },
  },
};
</script>

<template>
  <Layout>
    <PageHeader title="Campanhas" pageTitle="Campanhas" />

    <BCard no-body>
      <div class="card-body">
              <div class="d-flex justify-content-between align-items-center mb-3">
                <div class="text-muted small">
                  Total: <strong>{{ campanhas.length }}</strong> ·
                  Em execução: <strong>{{ porStatus.em_execucao || 0 }}</strong> ·
                  Pausadas: <strong>{{ porStatus.pausada || 0 }}</strong>
                </div>
                <BButton variant="success" @click="abrirNova">
                  <i class="ri-add-line align-bottom me-1"></i> Nova Campanha
                </BButton>
              </div>

              <div v-if="loading" class="text-center py-4"><BSpinner variant="primary" /></div>

              <div v-else class="table-responsive">
                <table class="table align-middle table-nowrap mb-0">
                  <thead class="table-light">
                    <tr>
                      <th>Nome</th>
                      <th>Tipo</th>
                      <th>Status</th>
                      <th>Número / Provedor</th>
                      <th>Público</th>
                      <th class="text-center">Progresso</th>
                      <th class="text-end">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="c in campanhas" :key="c.id">
                      <td>
                        <div class="fw-medium">{{ c.nome }}</div>
                        <small class="text-muted">{{ c.objetivo || '—' }}</small>
                      </td>
                      <td><span class="badge bg-info-subtle text-info">{{ canalLabel(c) }}</span></td>
                      <td>
                        <span :class="statusBadge(c.status)">{{ c.status }}</span>
                        <div v-if="c.status === 'em_execucao' && diagOf(c)" class="mt-1">
                          <span :class="diagBadgeClass(c)" :title="diagOf(c).acao">
                            <i :class="diagIcon(c)" class="align-bottom me-1"></i>{{ diagOf(c).rotulo }}
                          </span>
                          <div class="text-muted mt-1" style="font-size: 11px;">{{ diagDetalhe(c) }}</div>
                        </div>
                      </td>
                      <td>
                        <small v-if="ehRodizio(c)" class="text-muted">
                          <i class="ri-sim-card-2-line me-1"></i>Chips (QR code)
                        </small>
                        <small v-else-if="c.provedor && c.provedor !== 'meta_oficial'" class="text-muted">
                          <i class="ri-cloud-line me-1"></i>Allcance
                        </small>
                        <small v-else-if="c.canalWhatsapp">
                          {{ c.canalWhatsapp.telefone_formatado || c.canalWhatsapp.display_name }}
                          <span class="badge bg-light text-dark ms-1">tier {{ c.canalWhatsapp.tier_diario || '—' }}</span>
                        </small>
                        <small v-else class="text-muted">—</small>
                      </td>
                      <td>
                        <small>{{ c.publico_elegivel || 0 }} / {{ c.publico_estimado || 0 }}</small>
                      </td>
                      <td style="min-width: 220px;">
                        <div v-if="c.total_destinatarios > 0">
                          <div class="progress" style="height: 8px;">
                            <div class="progress-bar bg-success" :style="`width: ${pctEnviado(c)}%`"></div>
                            <div class="progress-bar bg-danger" :style="`width: ${pctFalha(c)}%`"></div>
                          </div>
                          <small class="text-muted">
                            {{ c.total_enviados || 0 }}/{{ c.total_destinatarios }}
                            · ✓ {{ c.total_entregues || 0 }}
                            · 👁 {{ c.total_lidos || 0 }}
                            · ✗ {{ c.total_falhas || 0 }}
                            <template v-if="!ehRodizio(c)">· 💬 {{ c.total_respondentes || 0 }}</template>
                          </small>
                        </div>
                        <small v-else class="text-muted">Sem fila</small>
                      </td>
                      <td class="text-end">
                        <div class="hstack gap-1 justify-content-end">
                          <BButton v-if="c.total_destinatarios > 0 && !ehRodizio(c)"
                            variant="soft-success" size="sm"
                            title="Ver respostas e relatório"
                            @click="verRespostas(c)">
                            <i class="ri-chat-poll-line"></i>
                          </BButton>
                          <BDropdown v-if="c.total_destinatarios > 0"
                            variant="soft-primary" size="sm" no-caret
                            :disabled="!!gerandoRelatorio[c.id]"
                            title="Relatório de disparo">
                            <template #button-content>
                              <BSpinner v-if="gerandoRelatorio[c.id]" small />
                              <i v-else class="ri-file-chart-line"></i>
                            </template>
                            <BDropdownHeader>Relatório de disparo</BDropdownHeader>
                            <BDropdownItem @click="relatorioDisparo(c, 'pdf')">
                              <i class="ri-file-pdf-2-line align-bottom me-2 text-danger"></i> PDF (resumo)
                            </BDropdownItem>
                            <BDropdownItem @click="relatorioDisparo(c, 'csv')">
                              <i class="ri-file-excel-2-line align-bottom me-2 text-success"></i> CSV (todos os destinatários)
                            </BDropdownItem>
                          </BDropdown>
                          <BButton variant="soft-info" size="sm" :title="'Editar / detalhes'" @click="abrirEdicao(c)">
                            <i class="ri-pencil-line"></i>
                          </BButton>
                          <BButton variant="soft-warning" size="sm"
                            :disabled="!['rascunho', 'validando', 'pronta'].includes(c.status)"
                            title="Validar público"
                            @click="validarPublico(c)">
                            <i class="ri-shield-check-line"></i>
                          </BButton>
                          <BButton v-if="['rascunho', 'pronta', 'validando'].includes(c.status)"
                            variant="soft-success" size="sm" title="Iniciar disparo" @click="iniciar(c)">
                            <i class="ri-send-plane-line"></i>
                          </BButton>
                          <BButton v-if="c.status === 'em_execucao' && podeDestravar(c)"
                            :variant="diagOf(c) && diagOf(c).travado ? 'soft-danger' : 'soft-primary'"
                            size="sm" title="Destravar e continuar"
                            :disabled="destravando[c.id]"
                            @click="destravar(c)">
                            <BSpinner v-if="destravando[c.id]" small />
                            <i v-else class="ri-flashlight-line"></i>
                          </BButton>
                          <BButton v-if="c.status === 'em_execucao'"
                            variant="soft-secondary" size="sm" title="Pausar" @click="pausar(c)">
                            <i class="ri-pause-line"></i>
                          </BButton>
                          <BButton v-if="c.status === 'pausada'"
                            variant="soft-success" size="sm" title="Retomar" @click="retomar(c)">
                            <i class="ri-play-line"></i>
                          </BButton>
                          <BButton v-if="['em_execucao', 'pausada'].includes(c.status)"
                            variant="soft-danger" size="sm" title="Cancelar" @click="cancelar(c)">
                            <i class="ri-stop-line"></i>
                          </BButton>
                          <BButton v-if="!['em_execucao', 'pausada'].includes(c.status) && !c.total_enviados"
                            variant="soft-danger" size="sm" title="Excluir campanha" @click="excluir(c)">
                            <i class="ri-delete-bin-line"></i>
                          </BButton>
                        </div>
                      </td>
                    </tr>
                    <tr v-if="!campanhas.length">
                      <td colspan="7" class="text-center text-muted py-4">Nenhuma campanha cadastrada.</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
    </BCard>

    <CampanhaModal
      :visivel="modalAberto"
      :campanha="campanhaEditando"
      @fechar="modalAberto = false; campanhaEditando = null"
      @salvo="onCampanhaSalva"
    />
  </Layout>
</template>
